import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { AlertTriangle } from 'lucide-react';

import { useSafeToast } from '@/hooks/useSafeToast';
import { useFundInvestors } from '@/hooks/react-query/useInvestors';
import { Alert } from '@/components/ui/alert';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { FundInvestorsToolbar } from '@/components/fund-detail/FundInvestorsToolbar';
import { FundInvestorsTable } from '@/components/fund-detail/FundInvestorsTable';
import { InvestorTransferModal } from '@/components/fund-detail/InvestorTransferModal';
import { EditCommitmentModal } from '@/components/fund-detail/EditCommitmentModal';
import { RemoveInvestorModal } from '@/components/fund-detail/RemoveInvestorModal';
import { getTransferorCandidates } from '@/components/fund-detail/investorTransferUtils';
import type { Fund } from '@/types/fund';
import type {
  InvestorDetail,
  InvestorFundRelationship,
} from '@/types/investor';
import type { InvestorFundTransferDraftPayload } from '@/types/investor-transfer';

interface FundInvestorsListProps {
  fund: Fund;
  fundInvestors?: InvestorDetail[];
  onAddInvestor: () => void;
  onInvestorsChange?: () => void;
  onInitiateTransfer?: (
    payload: InvestorFundTransferDraftPayload
  ) => Promise<void> | void;
  className?: string;
}

export type { InvestorFundTransferDraftPayload } from '@/types/investor-transfer';

export function FundInvestorsList({
  fund,
  fundInvestors: fundInvestorsProp,
  onAddInvestor,
  onInvestorsChange,
  onInitiateTransfer,
  className,
}: FundInvestorsListProps) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const { toast } = useSafeToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [editingRelationship, setEditingRelationship] =
    useState<InvestorFundRelationship | null>(null);
  const [removingInvestor, setRemovingInvestor] =
    useState<InvestorDetail | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const fundInvestorsQuery = useFundInvestors(fund.id, {
    enabled: !fundInvestorsProp,
  });

  const investors = useMemo(
    () => fundInvestorsProp || fundInvestorsQuery.data || [],
    [fundInvestorsProp, fundInvestorsQuery.data]
  );

  const isTransferConfigured = Boolean(onInitiateTransfer);
  const loading = fundInvestorsQuery.isLoading || fundInvestorsQuery.isFetching;
  const error = fundInvestorsQuery.error?.message;
  const isInitialLoading = loading && investors.length === 0;

  const filteredInvestors = useMemo(() => {
    const normalizedSearchTerm = searchTerm.toLowerCase();

    return investors.filter(
      (investor) =>
        investor.name.toLowerCase().includes(normalizedSearchTerm) ||
        investor.country?.toLowerCase().includes(normalizedSearchTerm) ||
        investor.id.toLowerCase().includes(normalizedSearchTerm)
    );
  }, [investors, searchTerm]);

  const transferorCandidates = useMemo(
    () => getTransferorCandidates(investors, fund.id),
    [fund.id, investors]
  );

  const handleOpenTransferModal = () => {
    if (!isTransferConfigured) {
      toast({
        title: 'Transfer unavailable',
        description: 'Transfer action is not configured.',
        variant: 'destructive',
      });
      return;
    }

    if (transferorCandidates.length === 0) {
      toast({
        title: 'Transfer unavailable',
        description:
          'No investor in this fund has an active commitment to transfer.',
        variant: 'destructive',
      });
      return;
    }

    setShowTransferModal(true);
  };

  const handleOpenCapitalAccounts = () => {
    setSearchParams((previousParams) => {
      const nextParams = new URLSearchParams(previousParams);
      nextParams.set('tab', 'general-ledger');
      nextParams.set('section', 'capital-accounts');
      return nextParams;
    });
  };

  const handleInvestorClick = (investor: InvestorDetail) => {
    navigate(`/app/investors/${investor.id}`);
  };

  const handleEditCommitment = (relationship: InvestorFundRelationship) => {
    setEditingRelationship(relationship);
  };

  const handleCloseEditModal = () => {
    setEditingRelationship(null);
  };

  const handleRemoveInvestor = (investor: InvestorDetail) => {
    setRemovingInvestor(investor);
  };

  const handleCloseRemoveModal = () => {
    setRemovingInvestor(null);
  };

  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
  };

  if (fundInvestorsQuery.isError) {
    return (
      <Card className={className}>
        <CardHeader />
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            {error || 'Failed to load investors'}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <FundInvestorsToolbar
            searchTerm={searchTerm}
            loading={loading}
            isTransferConfigured={isTransferConfigured}
            hasTransferCandidates={transferorCandidates.length > 0}
            onSearchTermChange={setSearchTerm}
            onRefresh={() => {
              void fundInvestorsQuery.refetch();
            }}
            onOpenCapitalAccounts={handleOpenCapitalAccounts}
            onAddInvestor={onAddInvestor}
            onOpenTransferModal={handleOpenTransferModal}
          />
        </CardHeader>

        <CardContent>
          {isInitialLoading && (
            <div className="py-8 text-center text-muted-foreground">
              Loading investors...
            </div>
          )}
          {!isInitialLoading && filteredInvestors.length === 0 && (
            <div className="py-8 text-center text-muted-foreground">
              {searchTerm
                ? 'No investors found matching your search'
                : 'No investors in this fund'}
            </div>
          )}
          {!isInitialLoading && filteredInvestors.length > 0 && (
            <FundInvestorsTable
              fund={fund}
              investors={filteredInvestors}
              loading={loading}
              onInvestorClick={handleInvestorClick}
              onEditCommitment={handleEditCommitment}
              onRemoveInvestor={handleRemoveInvestor}
            />
          )}
        </CardContent>
      </Card>

      {showTransferModal && (
        <InvestorTransferModal
          open={showTransferModal}
          fund={fund}
          investors={investors}
          loading={loading}
          onClose={handleCloseTransferModal}
          onInitiateTransfer={onInitiateTransfer}
          onSuccess={onInvestorsChange}
        />
      )}

      {editingRelationship && (
        <EditCommitmentModal
          open={Boolean(editingRelationship)}
          fund={fund}
          relationship={editingRelationship}
          onClose={handleCloseEditModal}
          onSaved={onInvestorsChange}
        />
      )}

      {removingInvestor && (
        <RemoveInvestorModal
          open={Boolean(removingInvestor)}
          fundId={fund.id}
          investor={removingInvestor}
          onClose={handleCloseRemoveModal}
          onRemoved={onInvestorsChange}
        />
      )}
    </>
  );
}
